import React, { useCallback, useEffect, useState } from "react";
import { Wallet, Loader2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { crud, hesapTransfer, kasaOzet } from "../../lib/api";

function fmtMoney(n, pb = "TRY") {
    const sym = pb === "EUR" ? "€" : pb === "USD" ? "$" : "₺";
    return `${sym}${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`;
}

export default function KasaHesaplarPage() {
    const [ozet, setOzet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [kaynakId, setKaynakId] = useState("");
    const [hedefId, setHedefId] = useState("");
    const [tutar, setTutar] = useState("");
    const [aciklama, setAciklama] = useState("");
    const [transferring, setTransferring] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await kasaOzet();
            setOzet(d);
        } catch {
            setOzet(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function transferYap(e) {
        e.preventDefault();
        if (!kaynakId || !hedefId || !tutar) {
            toast.error("Kaynak, hedef ve tutar gerekli.");
            return;
        }
        setTransferring(true);
        try {
            await hesapTransfer({
                kaynak_id: kaynakId,
                hedef_id: hedefId,
                tutar,
                aciklama,
            });
            toast.success("Transfer tamamlandı.");
            setTutar("");
            setAciklama("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Transfer başarısız.");
        } finally {
            setTransferring(false);
        }
    }

    const hesaplar = ozet?.hesaplar || [];

    return (
        <div className="p-6 max-w-5xl">
            <div className="mb-6">
                <h1 className="font-display text-2xl text-white flex items-center gap-2">
                    <Wallet className="h-6 w-6 text-[#D4AF37]" />
                    Kasa & Hesaplar
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Ofis kasa takibi — dosya ödemeleri otomatik hesaba düşer
                </p>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-slate-400 py-8">
                    <Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        {hesaplar.map((h) => (
                            <div
                                key={h.id}
                                className="rounded-xl border border-[#D4AF37]/25 bg-[#121A2F]/80 p-4"
                            >
                                <div className="text-xs text-slate-500 uppercase tracking-wider">
                                    {h.ad}
                                    {h.varsayilan && (
                                        <span className="ml-2 text-[#D4AF37]">varsayılan</span>
                                    )}
                                </div>
                                <div className="text-2xl font-semibold text-white mt-1">
                                    {fmtMoney(h.bakiye, h.para_birimi)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <section className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                                <ArrowRightLeft className="h-4 w-4 text-[#D4AF37]" />
                                Hesap Transferi
                            </h3>
                            <form onSubmit={transferYap} className="space-y-3">
                                <div>
                                    <label className="text-xs text-slate-500">Kaynak</label>
                                    <select
                                        value={kaynakId}
                                        onChange={(e) => setKaynakId(e.target.value)}
                                        className="w-full mt-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="">Seçin</option>
                                        {hesaplar.map((h) => (
                                            <option key={h.id} value={h.id}>
                                                {h.ad}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500">Hedef</label>
                                    <select
                                        value={hedefId}
                                        onChange={(e) => setHedefId(e.target.value)}
                                        className="w-full mt-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm"
                                    >
                                        <option value="">Seçin</option>
                                        {hesaplar.map((h) => (
                                            <option key={h.id} value={h.id}>
                                                {h.ad}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500">Tutar</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={tutar}
                                        onChange={(e) => setTutar(e.target.value)}
                                        className="w-full mt-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500">Açıklama</label>
                                    <input
                                        value={aciklama}
                                        onChange={(e) => setAciklama(e.target.value)}
                                        className="w-full mt-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm"
                                        placeholder="Avans, nakit çekim…"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={transferring}
                                    className="w-full h-10 rounded-md bg-[#D4AF37] text-slate-900 text-sm font-semibold disabled:opacity-50"
                                >
                                    {transferring ? "İşleniyor…" : "Transfer yap"}
                                </button>
                            </form>
                        </section>

                        <section className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                            <h3 className="text-sm font-semibold text-slate-300 mb-3">
                                Son Hareketler
                            </h3>
                            <ul className="space-y-2 max-h-80 overflow-y-auto text-sm">
                                {(ozet?.son_hareketler || []).map((m) => (
                                    <li
                                        key={m.id}
                                        className="flex items-start gap-2 border-b border-white/5 pb-2"
                                    >
                                        <span
                                            className={`shrink-0 font-mono text-xs ${
                                                m.tutar >= 0
                                                    ? "text-emerald-400"
                                                    : "text-rose-400"
                                            }`}
                                        >
                                            {m.tutar >= 0 ? "+" : ""}
                                            {Number(m.tutar).toLocaleString("tr-TR")}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="text-slate-300 truncate">
                                                {m.aciklama || m.hareket_turu}
                                            </div>
                                            <div className="text-[10px] text-slate-500">
                                                {m.hesap} ·{" "}
                                                {m.tarih
                                                    ? new Date(m.tarih).toLocaleDateString("tr-TR")
                                                    : ""}
                                                {m.dosya_no ? ` · ${m.dosya_no}` : ""}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>
                </>
            )}
        </div>
    );
}
